<template>
  <Modal :is-open="true" @close="$emit('cancel')">
    <template #header>
      <h2 class="text-xl font-semibold">
        {{ maintenance ? 'Edit Maintenance' : 'Schedule Maintenance' }}
      </h2>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <!-- Equipment Selection -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Equipment *</label>
        <select
          v-model="formData.equipment"
          required
          class="w-full p-2 border border-gray-300 rounded-md"
          :class="{ 'border-red-500': errors.equipment }"
        >
          <option value="">Select Equipment</option>
          <option v-for="eq in equipmentList" :key="eq.id" :value="eq.id">
            {{ eq.name }} ({{ eq.type }})
          </option>
        </select>
        <p v-if="errors.equipment" class="text-red-500 text-xs mt-1">{{ errors.equipment }}</p>
      </div>

      <!-- Maintenance Type and Date -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Maintenance Type *</label>
          <select
            v-model="formData.maintenance_type"
            required
            class="w-full p-2 border border-gray-300 rounded-md"
            :class="{ 'border-red-500': errors.maintenance_type }"
          >
            <option value="scheduled">Scheduled</option>
            <option value="repair">Repair</option>
            <option value="inspection">Inspection</option>
          </select>
          <p v-if="errors.maintenance_type" class="text-red-500 text-xs mt-1">
            {{ errors.maintenance_type }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Maintenance Date *</label>
          <input
            type="date"
            v-model="formData.maintenance_date"
            required
            class="w-full p-2 border border-gray-300 rounded-md"
            :class="{ 'border-red-500': errors.maintenance_date }"
          />
          <p v-if="errors.maintenance_date" class="text-red-500 text-xs mt-1">
            {{ errors.maintenance_date }}
          </p>
        </div>
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <textarea
          v-model="formData.description"
          required
          rows="3"
          class="w-full p-2 border border-gray-300 rounded-md"
          :class="{ 'border-red-500': errors.description }"
          placeholder="Describe the maintenance work..."
        ></textarea>
        <p v-if="errors.description" class="text-red-500 text-xs mt-1">{{ errors.description }}</p>
      </div>

      <!-- Cost and Technician -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Cost (TSH) *</label>
          <input
            type="number"
            v-model="formData.cost"
            step="0.01"
            min="0"
            required
            class="w-full p-2 border border-gray-300 rounded-md"
            :class="{ 'border-red-500': errors.cost }"
            placeholder="0.00"
          />
          <p v-if="errors.cost" class="text-red-500 text-xs mt-1">{{ errors.cost }}</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Technician</label>
          <input
            v-model="formData.technician"
            class="w-full p-2 border border-gray-300 rounded-md"
            placeholder="Technician name"
          />
        </div>
      </div>

      <!-- Next Maintenance Date -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Next Maintenance Date</label>
        <input
          type="date"
          v-model="formData.next_maintenance_date"
          class="w-full p-2 border border-gray-300 rounded-md"
        />
      </div>

      <!-- Notes -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          v-model="formData.notes"
          rows="2"
          class="w-full p-2 border border-gray-300 rounded-md"
          placeholder="Additional notes..."
        ></textarea>
      </div>

      <!-- Form Actions -->
      <div class="flex space-x-3 pt-4">
        <button
          type="submit"
          class="btn-primary flex-1"
          :disabled="loading"
          :class="{ 'opacity-50 cursor-not-allowed': loading }"
        >
          {{ loading ? 'Saving...' : maintenance ? 'Update' : 'Schedule' }}
        </button>
        <button
          type="button"
          @click="$emit('cancel')"
          class="btn-secondary flex-1"
          :disabled="loading"
        >
          Cancel
        </button>
      </div>
    </form>
  </Modal>
</template>

<script setup lang="ts">
import { reactive, watch, onMounted, computed } from 'vue'
import { useEquipmentStore } from '@/stores/equipmentStore'
import type { Maintenance, MaintenanceCreate } from '@/types/maintenance'
import Modal from '@/components/shared/Modal.vue'
import { useNotificationStore } from '@/stores/notification'

interface Props {
  maintenance?: Maintenance
  loading?: boolean
  equipment?: import('@/types/equipment').Equipment | null
}

interface Errors {
  equipment?: string
  maintenance_type?: string
  maintenance_date?: string
  description?: string
  cost?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'save', data: MaintenanceCreate): void
  (e: 'cancel'): void
}>()

const equipmentStore = useEquipmentStore()
const notificationStore = useNotificationStore()

// Updated form data structure to match backend
const formData = reactive({
  equipment: 0, // Changed from equipment_id
  maintenance_type: 'scheduled',
  description: '',
  cost: 0, // Changed from undefined to 0 (required field)
  maintenance_date: new Date().toISOString().split('T')[0],
  next_maintenance_date: undefined as string | undefined, // Changed from next_due_date
  technician: undefined as string | undefined,
  notes: undefined as string | undefined,
})

const errors = reactive<Errors>({})

// Populate form if editing existing maintenance
watch(
  () => props.maintenance,
  (maintenance) => {
    if (maintenance) {
      Object.assign(formData, {
        equipment: maintenance.equipment_id, // Map equipment_id to equipment
        maintenance_type: maintenance.maintenance_type,
        description: maintenance.description,
        cost: maintenance.cost || 0,
        maintenance_date: maintenance.maintenance_date,
        next_maintenance_date: maintenance.next_due_date, // Map to new field name from type
        technician: maintenance.technician,
        notes: maintenance.notes,
      })
    }
  },
  { immediate: true },
)

// If parent passes a selected equipment (e.g., from a stepper), prefill the equipment
watch(
  () => props.equipment,
  (eq) => {
    if (eq && eq.id) {
      formData.equipment = eq.id
    }
  },
  { immediate: true },
)

onMounted(() => {
  equipmentStore.fetchEquipment()
})

// Consider showing active equipment too, not just maintenance status
const equipmentList = computed(() =>
  equipmentStore.equipment.filter((eq) => eq.status === 'active' || eq.status === 'maintenance'),
)

const validateForm = (): boolean => {
  const newErrors: Errors = {}

  if (!formData.equipment) newErrors.equipment = 'Equipment is required'
  if (!formData.maintenance_type) newErrors.maintenance_type = 'Maintenance type is required'
  if (!formData.maintenance_date) newErrors.maintenance_date = 'Maintenance date is required'
  if (!formData.description.trim()) newErrors.description = 'Description is required'

  // Validate cost - ensure it's a valid number and not negative
  if (formData.cost === null || formData.cost === undefined) {
    newErrors.cost = 'Cost is required'
  } else if (isNaN(Number(formData.cost)) || Number(formData.cost) < 0) {
    newErrors.cost = 'Cost must be a valid non-negative number'
  }

  Object.assign(errors, newErrors)
  return Object.keys(newErrors).length === 0
}

const handleSubmit = async () => {
  if (validateForm()) {
    // Prepare data for backend - ensure all fields are properly formatted
    const submitData = {
      equipment_id: Number(formData.equipment) || 0,
      maintenance_type: formData.maintenance_type as 'scheduled' | 'repair' | 'inspection',
      description: formData.description,
      cost: Number(formData.cost) || 0,
      maintenance_date: formData.maintenance_date,
      next_due_date: formData.next_maintenance_date || undefined,
      technician: formData.technician?.trim() || undefined,
      notes: formData.notes?.trim() || undefined,
    }

    console.log('Submitting maintenance data:', submitData) // For debugging

    try {
      await emit('save', submitData)
    } catch (err: unknown) {
      console.error('Error submitting maintenance:', err)

      // Enhanced error handling
      // Try to extract axios style response safely
      const maybeErr = err as { response?: { data?: unknown } } | unknown
      if (typeof maybeErr === 'object' && maybeErr !== null && 'response' in maybeErr) {
        const backendErrors = (maybeErr as { response: { data: unknown } }).response.data
        let errorMsg = 'Failed to save maintenance:'

        if (backendErrors && typeof backendErrors === 'object') {
          const be = backendErrors as Record<string, unknown>
          // handle custom errors that have a details array
          const maybeDetails = be['details'] as unknown
          if (Array.isArray(maybeDetails)) {
            maybeDetails.forEach((d) => {
              if (typeof d === 'string') errorMsg += `\n${d}`
            })
          } else {
            for (const key in be) {
              const val = be[key]
              if (Array.isArray(val)) {
                ;(val as unknown[]).forEach((v) => {
                  if (typeof v === 'string') errorMsg += `\n${key}: ${v}`
                })
              } else if (typeof val === 'string' || typeof val === 'number') {
                errorMsg += `\n${key}: ${val}`
              }
            }
          }
        } else if (typeof backendErrors === 'string' || typeof backendErrors === 'number') {
          errorMsg += `\n${backendErrors}`
        }
        notificationStore.showError(errorMsg)
      } else {
        notificationStore.showError('Failed to save maintenance. Please check your input and try again.')
      }
    }
  }
}
</script>
